import { useCallback } from "react";

import { getStoredSoilSightProjectId, setStoredSoilSightProjectId, useNavigate } from "utils";

import type { DroppedMainCrop } from "models";

import { useFieldImportPersist } from "pages/app/scenario-analysis/hooks/useFieldImportPersist";
import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { isFieldImportReadyForRecalculation } from "pages/app/scenario-analysis/utils/scenarioValidation";

export type SubmitFieldImportArgs = {
  klimFarmId: string;
  fieldId: string;
  cropYears: CropYear[];
  /** When true the user already has projects — skip persistence and let ScenarioAnalysis show ProjectEntryDialog. */
  hasExistingProjects?: boolean;
};

export const useSoilSightFieldImportSubmit = () => {
  const navigate = useNavigate();
  const { persistFieldImport, refreshAfterImport, showImportError, isPersisting: isSubmitting } = useFieldImportPersist();

  const submitFieldImport = useCallback(
    async ({ klimFarmId, fieldId, cropYears, hasExistingProjects }: SubmitFieldImportArgs): Promise<boolean> => {
      try {
        const cropRotationLoaded = cropYears.some((year) => Boolean(year.crop?.trim()));

        if (hasExistingProjects) {
          // Existing projects: navigate without persisting so ScenarioAnalysis can show ProjectEntryDialog.
          navigate("/scenarios", {
            replace: true,
            state: {
              selectedFieldId: fieldId,
              importedCropYears: cropYears,
              showProjectEntryDialog: true,
              cropRotationLoaded,
              importReadyForRecalc: isFieldImportReadyForRecalculation(cropYears),
            },
          });
          return true;
        }

        let persistedProjectId: string | null = null;
        let importSucceeded = false;
        let droppedMainCrops: DroppedMainCrop[] = [];
        // Reflects what actually landed in the DB (pruned/padded by persistSoilSightsFieldImport),
        // not the raw pre-persist input — readiness must be judged against the persisted rotation.
        let persistedCropYears = cropYears;
        try {
          const result = await persistFieldImport({
            klimFarmId,
            fieldId,
            cropYears,
            getTargetProjectId: getStoredSoilSightProjectId,
            storeProjectId: (ssId) => {
              persistedProjectId = ssId;
              setStoredSoilSightProjectId(ssId);
            },
          });
          persistedProjectId = result.soilSightProjectId;
          droppedMainCrops = result.droppedMainCrops;
          persistedCropYears = result.cropYears;
          importSucceeded = true;
        } catch (importError) {
          /* crop year import may have failed after project creation */
          showImportError(importError);
        }

        if (!persistedProjectId) return false;

        // Refresh the server-side farm pointer (user.farm.soilSightProjectId) the /scenarios gate reads,
        // so it recognises the just-created project instead of redirecting and dropping the transition state.
        await refreshAfterImport({ soilSightProjectId: persistedProjectId, invalidateScenarios: importSucceeded });

        const importReadyForRecalc = importSucceeded && isFieldImportReadyForRecalculation(persistedCropYears);
        navigate("/scenarios", {
          replace: true,
          state: {
            selectedFieldId: fieldId,
            fieldImportAlreadyPersisted: importSucceeded,
            showFieldImportTransition: importSucceeded,
            cropRotationLoaded,
            importReadyForRecalc,
            droppedMainCrops,
          },
        });
        return true;
      } catch (submitError) {
        showImportError(submitError);
        return false;
      }
    },
    [navigate, persistFieldImport, refreshAfterImport, showImportError]
  );

  return { submitFieldImport, isSubmitting };
};
